import { autorun } from "mobx";
import { MerchantAccountsGateway, TaxStatus } from "../enums";
import {
  IAPICreateSellerModel,
  IAPILinkedSellerModel,
  IAPISellerCommunicationSettingsModel,
  IAPISellerLinkedMerchantAccountModel,
} from "../interfaces";
import {
  AuthService,
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  InputErrorModel,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { EditorState } from "draft-js";
import { CountryWithStateModel } from "./CountryModels";
import { MediaModel } from "./MediaModel";

export class CreateSellerModel extends CoreModel {
  id: string;
  name: string;
  isActive: boolean;
  email: string;
  phoneCountry: CountryWithStateModel;
  phoneNumber: string;
  countryDetails: CountryWithStateModel;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  taxStatus: keyof typeof TaxStatus;
  taxCountryDetails: CountryWithStateModel;
  taxId: string;
  logo?: MediaModel;
  communicationSettings: SellerCommunicationSettingsModel;
  merchantAccountId: string;
  linkedMerchants: SellerLinkedMerchantAccountModel[];
  termsAndConditions: string;
  termsAndConditionsEditorState: EditorState;
  termsAndConditionsEditorRawContent: string;
  returnPolicy: string;
  returnPolicyEditorState: EditorState;
  returnPolicyEditorRawContent: string;
  errors: InputErrorModel<CreateSellerModel>[];

  constructor(data?: Partial<CreateSellerModel>) {
    super();
    this.id = (data && data.id) || "";
    this.name = (data && data.name) || "";
    this.isActive = (data && data.isActive) || false;
    this.email = data?.email || "";
    this.phoneCountry =
      (data && data.phoneCountry) || new CountryWithStateModel();
    this.phoneNumber = data?.phoneNumber || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.address1 = data?.address1 || "";
    this.address2 = data?.address2 || "";
    this.city = data?.city || "";
    this.state = data?.state || "";
    this.zip = data?.zip || "";
    this.taxStatus = data?.taxStatus || TaxStatus.Taxable;
    this.taxCountryDetails =
      data?.taxCountryDetails || new CountryWithStateModel();
    this.taxId = data?.taxId || "";
    this.logo = data?.logo && new MediaModel(data?.logo);
    this.communicationSettings = new SellerCommunicationSettingsModel(
      data?.communicationSettings,
    );
    this.merchantAccountId = data?.merchantAccountId || "";
    this.linkedMerchants =
      data?.linkedMerchants?.map(
        (merchant) => new SellerLinkedMerchantAccountModel(merchant),
      ) || [];
    this.termsAndConditions = data?.termsAndConditions || "";
    this.termsAndConditionsEditorRawContent =
      data?.termsAndConditionsEditorRawContent || "";
    this.termsAndConditionsEditorState =
      data?.termsAndConditionsEditorState ||
      UtilityMethods.parseEditorState(
        data?.termsAndConditionsEditorRawContent || "",
      );
    this.returnPolicy = data?.returnPolicy || "";
    this.returnPolicyEditorRawContent =
      data?.returnPolicyEditorRawContent || "";
    this.returnPolicyEditorState =
      data?.returnPolicyEditorState ||
      UtilityMethods.parseEditorState(data?.returnPolicyEditorRawContent || "");
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CreateSellerModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  static deserializeAPIModel(
    data: IAPICreateSellerModel,
    countries: CountryWithStateModel[],
  ): CreateSellerModel {
    const selectedCountry = countries.find(
      (country) => country.id === data.address?.countryId,
    );
    const defaultCountry = countries.find(
      (country) => country.abbreviationName.toLowerCase() === "us",
    );
    const selectedPhoneCountry =
      data.phone.countryCode &&
      countries.find((country) => country.id === data.phone.countryId);
    const selectedTaxCountry =
      data.taxCountryId &&
      countries.find((country) => country.id === data.taxCountryId);
    const mappedData: Partial<CreateSellerModel> = {
      id: data.id,
      name: data.name,
      isActive: data.isActive,
      phoneCountry: selectedPhoneCountry
        ? new CountryWithStateModel(selectedPhoneCountry)
        : new CountryWithStateModel(defaultCountry),
      phoneNumber:
        data.phone && data.phone.phoneNumber ? data.phone.phoneNumber : "",
      countryDetails: selectedCountry
        ? new CountryWithStateModel(selectedCountry)
        : new CountryWithStateModel(defaultCountry),
      address1: data.address ? data.address.address1 : "",
      address2: data.address ? data.address.address2 : "",
      city: data.address ? data.address.city : "",
      state: data.address?.stateId || "",
      zip: data.address ? data.address.zipCode : "",
      email: data.email || "",
      taxId: data.taxId ? data.taxId : "",
      taxCountryDetails: selectedTaxCountry
        ? new CountryWithStateModel(selectedTaxCountry)
        : new CountryWithStateModel(defaultCountry),
      taxStatus: data.taxStatus
        ? (data.taxStatus as keyof typeof TaxStatus)
        : TaxStatus.Taxable,
      logo: data?.logo ? MediaModel.deserializeAPIModel(data?.logo) : undefined,
      communicationSettings: new SellerCommunicationSettingsModel(
        data?.communicationSettings,
      ),
      linkedMerchants:
        data?.linkedMerchants?.map(
          (merchant) => new SellerLinkedMerchantAccountModel(merchant),
        ) || [],
      termsAndConditions: data.termsAndConditions || "",
      termsAndConditionsEditorRawContent:
        data.termsAndConditionsEditorRawContent || "",
      termsAndConditionsEditorState: UtilityMethods.parseEditorState(
        data.termsAndConditionsEditorRawContent || "",
      ),
      returnPolicy: data.returnPolicy || "",
      returnPolicyEditorRawContent: data.returnPolicyEditorRawContent || "",
      returnPolicyEditorState: UtilityMethods.parseEditorState(
        data.returnPolicyEditorRawContent || "",
      ),
    };
    return new CreateSellerModel(mappedData);
  }

  hasError(field: keyof CreateSellerModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getError(field: keyof CreateSellerModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error?.message || "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CreateSellerModel>[] = [];
    CoreInputModelUtil.validateTextField(this.name, "name", newErrors);
    CoreInputModelUtil.validateEmail("email", this.email, newErrors);
    CoreInputModelUtil.validateTextField(this.address1, "address1", newErrors);
    CoreInputModelUtil.validateTextField(this.address2, "address2", newErrors);
    CoreInputModelUtil.validateTextField(this.city, "city", newErrors);
    CoreInputModelUtil.validateTextField(this.state, "state", newErrors);
    CoreInputModelUtil.validateTextField(this.zip, "zip", newErrors);
    CoreInputModelUtil.validateTextField(
      this.phoneCountry.name,
      "phoneCountry",
      newErrors,
    );
    CoreInputModelUtil.validatePhoneNumber(
      "phoneNumber",
      this.phoneNumber,
      newErrors,
    );
    CoreInputModelUtil.validateTextField(
      this.countryDetails.name,
      "countryDetails",
      newErrors,
    );
    CoreInputModelUtil.validateTextField(
      this.taxStatus,
      "taxStatus",
      newErrors,
    );
    CoreInputModelUtil.validateTextField(
      this.taxCountryDetails.name,
      "taxCountryDetails",
      newErrors,
    );
    if (TaxStatus[this.taxStatus] === TaxStatus.TaxExempt) {
      CoreInputModelUtil.validateTaxId("taxId", this.taxId, newErrors);
    }

    const isValidCommunicationSettings =
      this.communicationSettings.validate(isAutoRun);
    if (!isValidCommunicationSettings) {
      this.addError("communicationSettings", newErrors);
    }
    // const isValidLogo = this.logo.validate();
    // if (!isValidLogo) {
    //   this.addError("logo", newErrors, "Logo is required field");
    // }
    const invalidLinkedMerchants = this.linkedMerchants.some(
      (merchant) => !merchant.validate(isAutoRun),
    );
    if (invalidLinkedMerchants) {
      this.addError("linkedMerchants", newErrors);
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPICreateSellerModel {
    return {
      id: this.id,
      isActive: this.isActive,
      email: this.email,
      phone: {
        countryCode: this.phoneCountry.code.toString(),
        countryId: this.phoneCountry.id,
        phoneNumber: this.phoneNumber,
      },
      address: {
        country: this.countryDetails.name,
        countryId: this.countryDetails.id,
        city: this.city,
        zipCode: this.zip,
        countryCode: this.countryDetails.code.toString(),
        stateId: this.state,
        state:
          this.countryDetails.states.find((state) => state.id === this.state)
            ?.name || "",
        address1: this.address1,
        address2: this.address2,
        address3: "",
      },
      accountId: AuthService.authData.accountId,
      name: this.name,
      taxStatus: this.taxStatus,
      taxCountryId: this.taxCountryDetails.id,
      taxId: this.taxId,
      logo: this.logo?.createAPIModel(),
      communicationSettings: this.communicationSettings?.createAPIModel(),
      linkedMerchants:
        this.linkedMerchants?.map((merchant) => merchant.createAPIModel()) ||
        [],
      termsAndConditions: this.termsAndConditions,
      termsAndConditionsEditorRawContent:
        this.termsAndConditionsEditorRawContent,
      returnPolicy: this.returnPolicy,
      returnPolicyEditorRawContent: this.returnPolicyEditorRawContent,
    };
  }
}

export class SellerCommunicationSettingsModel {
  fromEmail: string;
  fromText: string;
  errors: InputErrorModel<SellerCommunicationSettingsModel>[];
  constructor(data?: Partial<SellerCommunicationSettingsModel>) {
    this.fromEmail = data?.fromEmail || "";
    this.fromText = data?.fromText || "";
    this.errors = data?.errors || [];
  }
  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<SellerCommunicationSettingsModel>[] = [];
    CoreInputModelUtil.validateEmail("fromEmail", this.fromEmail, newErrors);
    CoreInputModelUtil.validateTextField(this.fromText, "fromText", newErrors);
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
  hasError(field: keyof SellerCommunicationSettingsModel): boolean {
    return this.errors.some((e) => e.field === field);
  }
  deserializeAPIModel(
    data?: IAPISellerCommunicationSettingsModel,
  ): SellerCommunicationSettingsModel {
    const mappedData: Partial<SellerCommunicationSettingsModel> = {
      fromEmail: data?.fromEmail || "",
      fromText: data?.fromText || "",
    };
    return new SellerCommunicationSettingsModel(mappedData);
  }
  createAPIModel(): IAPISellerCommunicationSettingsModel {
    return { fromEmail: this.fromEmail, fromText: this.fromText };
  }
}

export class SellerLinkedMerchantAccountModel {
  gateway: string;
  merchantAccountId: string;
  applicationId: string;
  name: string;
  isPrimary: boolean;
  errors: InputErrorModel<SellerLinkedMerchantAccountModel>[];
  constructor(data?: Partial<SellerLinkedMerchantAccountModel>) {
    this.gateway = data?.gateway || "";
    this.merchantAccountId = data?.merchantAccountId || "";
    this.applicationId = data?.applicationId || "";
    this.name = data?.name || "";
    this.isPrimary = data?.isPrimary || false;
    this.errors = data?.errors || [];
  }
  deserializeAPIModel(
    data?: IAPISellerLinkedMerchantAccountModel,
  ): SellerLinkedMerchantAccountModel {
    const mappedData: Partial<SellerLinkedMerchantAccountModel> = {
      gateway: data?.gateway || "",
      merchantAccountId: data?.merchantAccountId || "",
      applicationId: data?.applicationId || "",
      name: data?.name || "",
      isPrimary: data?.isPrimary || false,
    };
    return new SellerLinkedMerchantAccountModel(mappedData);
  }
  createAPIModel(): IAPISellerLinkedMerchantAccountModel {
    return {
      gateway: this.gateway as keyof typeof MerchantAccountsGateway,
      merchantAccountId: this.merchantAccountId,
      applicationId: this.applicationId,
      name: this.name,
      isPrimary: this.isPrimary,
      collectJSKey: "",
    };
  }
  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<SellerLinkedMerchantAccountModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.merchantAccountId,
      "merchantAccountId",
      newErrors,
    );
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
}
/**
 * Whether a rich-text policy actually has copy in it.
 *
 * An editor that was opened but never written to still serializes to a full
 * raw-content document - a blocks array carrying one empty block - so a
 * non-empty raw string is no evidence that the seller wrote anything. Only text
 * in the blocks, or an entity such as an embedded image, counts. Anything that
 * is not parseable raw content is copy the seller saved as plain text.
 */
export function hasEditorCopy(rawContent?: string): boolean {
  if (!rawContent?.trim()) return false;
  if (!CoreInputModelUtil.isValidJSON(rawContent)) return true;
  const raw = JSON.parse(rawContent);
  const blocks: { text?: string }[] = raw?.blocks || [];
  return (
    blocks.some((block) => !!block?.text?.trim()) ||
    Object.keys(raw?.entityMap || {}).length > 0
  );
}

/* What the constructor accepts. Callers pass the API shape straight in, where a policy the seller
   has not saved yet comes back as null rather than absent, so every field is allowed to be null
   here. The constructor coalesces all of them, which is why the model's own fields stay non-null
   and consumers never have to null-check a policy. */
type SellerModelInit = { [K in keyof SellerModel]?: SellerModel[K] | null };

export class SellerModel {
  id: string;
  name: string;
  isActive: boolean;
  logoURL: string;
  termsAndConditions: string;
  termsAndConditionsEditorRawContent: string;
  returnPolicy: string;
  returnPolicyEditorRawContent: string;
  /* The seller has a policy when there is copy to show for it - both fields are
     null until one is saved. Derived here as real properties rather than
     getters: a seller travels through object spreads and store snapshots that
     drop the prototype, and a lost getter reads as undefined, which silently
     hides the policy options in the builders. Recomputed on every construction,
     so a stale value carried in by a spread cannot survive. */
  hasTermsAndConditions: boolean;
  hasReturnPolicy: boolean;

  constructor(data?: SellerModelInit) {
    this.id = (data && data.id) || "";
    this.name = (data && data.name) || "";
    this.isActive = (data && data.isActive) || false;
    this.logoURL = data?.logoURL || "";
    this.termsAndConditions = data?.termsAndConditions || "";
    this.termsAndConditionsEditorRawContent =
      data?.termsAndConditionsEditorRawContent || "";
    this.returnPolicy = data?.returnPolicy || "";
    this.returnPolicyEditorRawContent =
      data?.returnPolicyEditorRawContent || "";
    this.hasTermsAndConditions =
      hasEditorCopy(this.termsAndConditionsEditorRawContent) ||
      !!this.termsAndConditions.trim();
    this.hasReturnPolicy =
      hasEditorCopy(this.returnPolicyEditorRawContent) ||
      !!this.returnPolicy.trim();
  }

  static deserializeAPIModel(data: IAPILinkedSellerModel): SellerModel {
    if (!data) {
      return new SellerModel();
    }

    const mappedData: Partial<SellerModel> = {
      id: data.id,
      name: data.name,
      isActive: data.isActive,
      logoURL: data.logoURL || "",
      termsAndConditions: data.termsAndConditions || "",
      termsAndConditionsEditorRawContent:
        data.termsAndConditionsEditorRawContent || "",
      returnPolicy: data.returnPolicy || "",
      returnPolicyEditorRawContent: data.returnPolicyEditorRawContent || "",
    };
    return new SellerModel(mappedData);
  }
  createAPIModel(): IAPILinkedSellerModel {
    return {
      id: this.id,
      isActive: this.isActive,
      name: this.name,
      logoURL: this.logoURL,
      termsAndConditions: this.termsAndConditions,
      termsAndConditionsEditorRawContent:
        this.termsAndConditionsEditorRawContent,
      returnPolicy: this.returnPolicy,
      returnPolicyEditorRawContent: this.returnPolicyEditorRawContent,
    };
  }
}

/* A seller reaches the builders from two directions: GetSellerNames, which
   returns the seller's identity only, and the checkout / purchase link itself
   (and Seller/Get), which return the policy copy with it. The helpers below
   move that copy between the two so an entry without it never masks one that
   has it - the case that hides the Terms dropdowns for a seller whose terms
   and return policy do exist. */

export function hasSellerPolicyCopy(seller?: SellerModel | null): boolean {
  return !!(seller?.hasTermsAndConditions || seller?.hasReturnPolicy);
}

export function sellerPolicyCopy(
  seller?: SellerModel | null,
): Partial<SellerModel> {
  return {
    termsAndConditions: seller?.termsAndConditions || "",
    termsAndConditionsEditorRawContent:
      seller?.termsAndConditionsEditorRawContent || "",
    returnPolicy: seller?.returnPolicy || "",
    returnPolicyEditorRawContent: seller?.returnPolicyEditorRawContent || "",
  };
}

/**
 * The seller as `base` identifies it, carrying whichever policy copy is
 * actually available: its own when it has some, otherwise `fallback`'s. Returns
 * `base` untouched when there is nothing to add, so identity is preserved and
 * observers are not woken for no reason.
 */
export function withSellerPolicyCopy(
  base: SellerModel,
  fallback?: SellerModel | null,
): SellerModel {
  if (hasSellerPolicyCopy(base) || !hasSellerPolicyCopy(fallback)) {
    return base;
  }
  return new SellerModel({ ...base, ...sellerPolicyCopy(fallback) });
}
